import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import PerformanceGraph from './PerformanceGraph';
import RecentCompressions from './RecentCompressions';
import { PageHeader, StatCard, Card, CardHeader, CardContent, PromoCard, Button } from '../ui';
import { useCredits } from '../../contexts/CreditContext';
import { get } from '../../api';

const Dashboard = () => {
  const [hack, setHack] = useState(0);
  const [totals, setTotals] = useState({ jobs: 0, avgRatio: 0, last7d: 0 });
  const uploadRef = useRef(null);
  const [search, setSearch] = useState('');
  const { credits, fetchCredits } = useCredits();
  const navigate = useNavigate();

  useEffect(() => {
    // Prefetch credits so StatCard shows up-to-date number
    if (fetchCredits) fetchCredits();
  }, [fetchCredits]);

  const fetchTotals = useCallback(async () => {
    try {
      const perf = await get('/api/1/jobs/performance');
      const performance = perf.performance || [];
      const jobs = performance.reduce((acc, d) => acc + (d.count || 0), 0);
      const last7 = performance.slice(-7).reduce((acc, d) => acc + (d.count || 0), 0);
      const avgRatio = performance.length
        ? (performance.reduce((acc, d) => acc + (d.avg_compression_ratio || 0), 0) / performance.length) * 100
        : 0;
      setTotals({ jobs, avgRatio, last7d: last7 });
    } catch (e) {
      // Fail silently for stats; keep dashboard calm
    }
  }, []);

  // Initial totals on mount
  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  // Refresh stats and credits whenever uploads occur (hack increments via onUpload)
  useEffect(() => {
    if (hack > 0) {
      fetchTotals();
      if (fetchCredits) fetchCredits();
    }
  }, [hack, fetchTotals, fetchCredits]);

  // Also listen for global job updates to refresh in real time across components
  useEffect(() => {
    const handler = () => {
      fetchTotals();
      if (fetchCredits) fetchCredits();
    };
    window.addEventListener('jobs:updated', handler);
    return () => window.removeEventListener('jobs:updated', handler);
  }, [fetchTotals, fetchCredits]);

  const creditsValue = useMemo(() =>
    credits === null || credits === undefined ? '—' : credits,
  [credits]);

  // Derive numeric credits for logic without breaking UI when unknown
  const creditsNum = useMemo(() => {
    if (typeof credits === 'number') return credits;
    if (typeof credits === 'string' && credits.trim() !== '' && !Number.isNaN(Number(credits))) {
      return Number(credits);
    }
    return null; // unknown
  }, [credits]);

  const isZeroCredits = Number.isFinite(creditsNum) && creditsNum <= 0;
  // Low-credits only applies after the user has actually uploaded at least one job
  const isLowCredits = Number.isFinite(creditsNum)
    && creditsNum > 0
    && creditsNum < 10
    && totals.jobs > 0;

  // Build a polished CTA card with clear affordances
  const promo = (() => {
    if (isZeroCredits) {
      return {
        title: "You're out of credits",
        description: 'To continue compressing models, add credits to your account.',
        note: 'Pay‑as‑you‑go. Credits never expire.',
        badge: 'Action required',
        buttonText: 'Get credits',
        onButtonClick: () => navigate({ to: '/app/billing' }),
        className: 'border-danger/40 ring-1 ring-danger/30',
      };
    }
    if (isLowCredits) {
      return {
        title: 'Low credits',
        description: `You have ${creditsNum} credits left. Top up now to avoid interruptions.`,
        note: 'You can still upload while you have credits remaining.',
        badge: 'Heads up',
        buttonText: 'Upload file',
        onButtonClick: () => uploadRef.current?.openUpload(),
        secondaryButtonText: 'Add credits',
        onSecondaryClick: () => navigate({ to: '/app/billing' }),
        className: 'border-accent/40',
      };
    }
    return {
      title: 'Compress your next model',
      description: 'Upload a .glb or .gltf to get started.',
      buttonText: 'Upload file',
      onButtonClick: () => uploadRef.current?.openUpload(),
      className: '',
    };
  })();

  return (
    <div className="space-y-8">
      <PageHeader
        title="Dashboard"
        subtitle="Welcome back. Compress your 3D assets and optimize your workflow."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <StatCard label="Credits available" value={creditsValue} hint="Usage resets only when you purchase more" />
        <StatCard label="Total jobs" value={totals.jobs.toLocaleString()} hint="All-time completed compressions" />
        <StatCard label="Avg compression ratio" value={`${totals.avgRatio.toFixed(1)}%`} hint="Across recent runs" />
        <StatCard label="Jobs (last 7 days)" value={totals.last7d.toLocaleString()} hint="Recent activity snapshot" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3">
          <CardHeader title="Compression performance" subtitle="Average compression ratio and job volume" />
          <CardContent>
            <PerformanceGraph embedded />
          </CardContent>
        </Card>
        <div className="lg:col-span-1">
          <PromoCard
            title={promo.title}
            description={promo.description}
            badge={promo.badge}
            note={promo.note}
            buttonText={promo.buttonText}
            onButtonClick={promo.onButtonClick}
            secondaryButtonText={promo.secondaryButtonText}
            onSecondaryClick={promo.onSecondaryClick}
            className={promo.className}
          />
        </div>
      </div>

      <Card>
        <CardHeader
          title="Recent compressions"
          subtitle="Latest files you processed"
          actions={
            <div className="flex items-center gap-3">
              {/* Search box controlling child */}
              <input
                type="text"
                placeholder="Search by filename..."
                className="w-64 bg-background-soft border border-border/60 rounded-lg px-3.5 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button
                variant="secondary"
                onClick={() => uploadRef.current?.openUpload()}
              >
                Upload files
              </Button>
            </div>
          }
        />
        <CardContent>
          <RecentCompressions
            ref={uploadRef}
            embedded
            searchTerm={search}
            onUpload={() => setHack((v) => v + 1)}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
